
import { buildChatTurns, buildTimeline } from "../shared/timeline.js";
import type { TimelineMessage } from "../shared/timeline.js";
import { sidePanel } from "../stores/side-panel.js";
import { threadStream, type Subagent } from "../stores/thread-stream.js";
import { buildSubagentTimeline } from "./chat-timeline.js";
import { AppChatView } from "./app-chat-view.js";
import { IconChevronLeft } from "./icons/chevron-left.js";
import { loadSubagentTranscript, loadSubagents } from "./subagent-data.js";

const styles = css`
  :host { display: block; height: 100%; min-height: 0; }
  .view { display: flex; flex-direction: column; height: 100%; min-height: 0; }
  .detail-header { align-items: center; border-bottom: 1px solid var(--border); display: flex; gap: 8px; min-height: 44px; padding: 0 12px; }
  .back { align-items: center; background: transparent; border: 0; border-radius: 6px; color: var(--muted-foreground); cursor: pointer; display: inline-flex; height: 28px; justify-content: center; padding: 0; width: 28px; }
  .back:hover { background: var(--muted); color: var(--foreground); }
  .back svg { height: 16px; width: 16px; }
  .detail-title { font-size: 13px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .list { overflow: auto; padding: 8px; }
  .row { align-items: center; background: transparent; border: 0; border-radius: 7px; color: inherit; cursor: pointer; display: grid; gap: 2px 8px; grid-template-columns: minmax(0, 1fr) auto; padding: 9px 10px; text-align: left; width: 100%; }
  .row:hover { background: var(--muted); }
  .title { font-size: 13px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .meta { color: var(--muted-foreground); font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .status { align-items: center; color: var(--muted-foreground); display: inline-flex; grid-row: 1 / span 2; grid-column: 2; }
  .status svg { height: 13px; width: 13px; }
  .pulse { background: var(--primary); border-radius: 999px; height: 7px; width: 7px; animation: pulse 1.4s ease-in-out infinite; }
  .empty { color: var(--muted-foreground); font-size: 13px; line-height: 1.5; padding: 32px 20px; text-align: center; }
  .result { color: var(--foreground); font-size: 14px; line-height: 1.6; overflow: auto; padding: 16px; white-space: pre-wrap; }
  app-chat-view { flex: 1; min-height: 0; }
  @keyframes pulse { 50% { opacity: .35; transform: scale(.8); } }
  @media (prefers-reduced-motion: reduce) { .pulse { animation: none; } }
`;

export class AppSubagentView extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppSubagentView)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["threadid","data-thread-id","active","data-active","agents","data-agents","selectedkey","data-selected-key","refreshtoken","data-refresh-token","transcript","data-transcript"];

  #threadId = "";
  #active = false;
  #agents = [];
  #selectedKey = "";
  #refreshToken = 0;
  #transcript = [];
  #threadIdSet = false;
  #activeSet = false;
  #agentsSet = false;
  #selectedKeySet = false;
  #refreshTokenSet = false;
  #transcriptSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #eventContexts = {};
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get threadId() {
    return this.#threadId;
  }

  set threadId(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#threadId, next)) return;
    this.#threadId = next;
    this.#threadIdSet = true;
    this.#updateBindings('threadId');
  }

  get active() {
    return this.#active;
  }

  set active(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#active, next)) return;
    this.#active = next;
    this.#activeSet = true;
    this.#updateBindings('active');
  }

  get agents() {
    return this.#agents;
  }

  set agents(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#agents, next)) return;
    this.#agents = next;
    this.#agentsSet = true;
    this.#updateBindings('agents');
  }

  get selectedKey() {
    return this.#selectedKey;
  }

  set selectedKey(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#selectedKey, next)) return;
    this.#selectedKey = next;
    this.#selectedKeySet = true;
    this.#updateBindings('selectedKey');
  }

  get refreshToken() {
    return this.#refreshToken;
  }

  set refreshToken(value) {
    const raw = value ?? 0;
    const next = raw == null ? raw : Number(raw);
    if (Object.is(this.#refreshToken, next)) return;
    this.#refreshToken = next;
    this.#refreshTokenSet = true;
    this.#updateBindings('refreshToken');
  }

  get transcript() {
    return this.#transcript;
  }

  set transcript(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#transcript, next)) return;
    this.#transcript = next;
    this.#transcriptSet = true;
    this.#updateBindings('transcript');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-thread-id')) this.threadId = this.dataset.threadId;
    if (this.hasAttribute('data-active')) this.active = this.dataset.active;
    if (this.hasAttribute('data-agents')) this.agents = this.dataset.agents;
    if (this.hasAttribute('data-selected-key')) this.selectedKey = this.dataset.selectedKey;
    if (this.hasAttribute('data-refresh-token')) this.refreshToken = this.dataset.refreshToken;
    if (this.hasAttribute('data-transcript')) this.transcript = this.dataset.transcript;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.threadId = props.threadId;
      this.active = props.active;
      this.agents = props.agents;
      this.selectedKey = props.selectedKey;
      this.refreshToken = props.refreshToken;
      this.transcript = props.transcript;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#threadIdSet) this.threadId = this.dataset.threadId;
    if (!this.#activeSet) this.active = this.dataset.active;
    if (!this.#agentsSet) this.agents = this.dataset.agents;
    if (!this.#selectedKeySet) this.selectedKey = this.dataset.selectedKey;
    if (!this.#refreshTokenSet) this.refreshToken = this.dataset.refreshToken;
    if (!this.#transcriptSet) this.transcript = this.dataset.transcript;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    const updateTranscript = (event: Event) => {
        const item = (event as CustomEvent<{
            item: TimelineMessage;
        }>).detail.item;
        this.transcript = [...this.transcript.filter((candidate: TimelineMessage) => candidate.id !== item.id), item]
            .sort((left: TimelineMessage, right: TimelineMessage) => left.seq - right.seq);
    };
    this.addEventListener("transcript-update", updateTranscript);
    return () => this.removeEventListener("transcript-update", updateTranscript);
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
    this.#effect0();
    this.#effect1();
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
    if (this.#effect0Cleanup) { this.#effect0Cleanup(); this.#effect0Cleanup = undefined; }
    if (this.#effect1Cleanup) { this.#effect1Cleanup(); this.#effect1Cleanup = undefined; }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'threadid':
      case 'data-thread-id':
        this.threadId = newValue;
        break;
      case 'active':
      case 'data-active':
        this.active = newValue;
        break;
      case 'agents':
      case 'data-agents':
        this.agents = newValue;
        break;
      case 'selectedkey':
      case 'data-selected-key':
        this.selectedKey = newValue;
        break;
      case 'refreshtoken':
      case 'data-refresh-token':
        this.refreshToken = newValue;
        break;
      case 'transcript':
      case 'data-transcript':
        this.transcript = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    const selected = this.#agents.find((agent) => agent.agentPath === this.#selectedKey);
    const sortedAgents = [...this.#agents]
    .sort((left, right) => right.createdAt - left.createdAt)
    .map((agent) => ({
    ...agent,
    indent: `padding-left:${10 + Math.max(0, agent.agentPath.split("/").length - 1) * 16}px`,
    summary: agent.finished && agent.result ? ` · ${agent.result.split("\n")[0].slice(0, 64)}` : "",
    statusLabel: agent.finished ? "Finished" : "Running",
    statusClass: agent.finished ? "done" : "pulse",
    statusGlyph: agent.finished ? "✓" : "",
}));
    const turns = selected ? buildChatTurns(buildTimeline(this.#transcript), !selected.finished) : [];
    const emptyTitle = this._loadingAgent ? "Loading transcript" : "No transcript available";
    const emptyDescription = !this._loadingAgent && selected?.result ? selected.result : "";
    return `<style>${this.#escText(styles)}</style><div class="view"><!--argon-text:0-->${selected ? `<div class="detail-header"><button class="back" type="button" aria-label="Back to subagents" data-argon="5319"><icon-chevron-left>${""}</icon-chevron-left></button><span class="detail-title">${selected.name}</span></div>${(turns.length > 0) ? `<app-chat-view data-turns="${this.#attrEsc(turns)}" data-empty-title="${this.#attrEsc(emptyTitle)}" data-empty-description="${this.#attrEsc(emptyDescription)}">${""}</app-chat-view>` : selected.result ? `<div class="result">${selected.result}</div>` : `<app-chat-view data-turns="${this.#attrEsc(turns)}" data-empty-title="${this.#attrEsc(emptyTitle)}" data-empty-description="${this.#attrEsc(emptyDescription)}">${""}</app-chat-view>`}` : (sortedAgents.length === 0) ? "<div class=\"empty\">No subagents yet.<br>Subagents appear here when a task delegates work.</div>" : `<div class="list">${sortedAgents.map((agent, __argonIndex) => this.#mapEventItem(`<button class="row" type="button" style="${agent.indent}" data-argon="6038"><span class="title">${agent.name}</span><span class="meta">${agent.model}${agent.summary}</span><span class="status" title="${agent.statusLabel}"><span class="${agent.statusClass}">${agent.statusGlyph}</span></span></button>`, "initial:1:6038:" + __argonIndex, { 6038: () => { sidePanel.selectedSubagent = agent.agentPath; this._loadedAgent = ""; this.selectedKey = agent.agentPath; } }, ["6038"])).join("")}</div>`}<!--/argon-text:0--></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
      }
    }
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #attrRaw(v) {
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }

  #attrEsc(v) {
    return this.#attrRaw(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #effect0Cleanup;
  #effect0() {
    if (this.#effect0Cleanup) this.#effect0Cleanup();
    const cleanup = (() => {
    if (!this.#active || !this.#threadId || this._agentsLoadedThread === this.#threadId)
        return;
    this._agentsLoadedThread = this.#threadId;
    void loadSubagents(this.#threadId).then(() => {
        this.agents = [...threadStream.subagents];
    });
})();
    this.#effect0Cleanup = typeof cleanup === 'function' ? cleanup : undefined;
  }

  #effect1Cleanup;
  #effect1() {
    if (this.#effect1Cleanup) this.#effect1Cleanup();
    const cleanup = (() => {
    const selectedAgent = this.#agents.find((agent) => agent.agentPath === this.#selectedKey);
    if (!this.#active || !this.#threadId || !selectedAgent || this._loadedAgent === selectedAgent.agentPath)
        return;
    this._loadedAgent = selectedAgent.agentPath;
    this._loadingAgent = selectedAgent.agentPath;
    void loadSubagentTranscript(this.#threadId, selectedAgent.id, selectedAgent.agentPath).finally(() => {
        this._loadingAgent = "";
        this.transcript = buildSubagentTimeline(selectedAgent.agentPath);
        this.refreshToken = this.#refreshToken + 1;
    });
})();
    this.#effect1Cleanup = typeof cleanup === 'function' ? cleanup : undefined;
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      this.#eventHandler(target, id, handler).call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #mapEventItem(html, ctx, handlers, ids) {
    const key = String(ctx);
    this.#eventContexts[key] = handlers;
    const attr = key.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    let out = String(html);
    for (const id of ids) out = out.replaceAll(`data-argon="${id}"`, `data-argon="${id}" data-argon-c="${attr}"`);
    return out;
  }

  #eventHandler(el, id, fallback) {
    const ctx = el.getAttribute('data-argon-c');
    return ctx && this.#eventContexts[ctx]?.[id] ? this.#eventContexts[ctx][id] : fallback;
  }


  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const selected = this.#agents.find((agent) => agent.agentPath === this.#selectedKey);
    const sortedAgents = [...this.#agents]
    .sort((left, right) => right.createdAt - left.createdAt)
    .map((agent) => ({
    ...agent,
    indent: `padding-left:${10 + Math.max(0, agent.agentPath.split("/").length - 1) * 16}px`,
    summary: agent.finished && agent.result ? ` · ${agent.result.split("\n")[0].slice(0, 64)}` : "",
    statusLabel: agent.finished ? "Finished" : "Running",
    statusClass: agent.finished ? "done" : "pulse",
    statusGlyph: agent.finished ? "✓" : "",
}));
    const turns = (prop === undefined || prop === 'agents' || prop === 'selectedKey' || prop === 'transcript') ? (selected ? buildChatTurns(buildTimeline(this.#transcript), !selected.finished) : []) : undefined;
    const emptyTitle = this._loadingAgent ? "Loading transcript" : "No transcript available";
    const emptyDescription = !this._loadingAgent && selected?.result ? selected.result : "";
    if (prop === undefined || prop === 'agents' || prop === 'selectedKey' || prop === 'transcript') {
      const __argonNext = `${selected ? `<div class="detail-header"><button class="back" type="button" aria-label="Back to subagents" data-argon="5319"><icon-chevron-left>${""}</icon-chevron-left></button><span class="detail-title">${selected.name}</span></div>${(turns.length > 0) ? `<app-chat-view data-turns="${this.#attrEsc(turns)}" data-empty-title="${this.#attrEsc(emptyTitle)}" data-empty-description="${this.#attrEsc(emptyDescription)}">${""}</app-chat-view>` : selected.result ? `<div class="result">${selected.result}</div>` : `<app-chat-view data-turns="${this.#attrEsc(turns)}" data-empty-title="${this.#attrEsc(emptyTitle)}" data-empty-description="${this.#attrEsc(emptyDescription)}">${""}</app-chat-view>`}` : (sortedAgents.length === 0) ? "<div class=\"empty\">No subagents yet.<br>Subagents appear here when a task delegates work.</div>" : `<div class="list">${sortedAgents.map((agent, __argonIndex) => this.#mapEventItem(`<button class="row" type="button" style="${agent.indent}" data-argon="6038"><span class="title">${agent.name}</span><span class="meta">${agent.model}${agent.summary}</span><span class="status" title="${agent.statusLabel}"><span class="${agent.statusClass}">${agent.statusGlyph}</span></span></button>`, "0:1:6038:" + __argonIndex, { 6038: () => { sidePanel.selectedSubagent = agent.agentPath; this._loadedAgent = ""; this.selectedKey = agent.agentPath; } }, ["6038"])).join("")}</div>`}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (this.#hooksLive && (prop === 'active' || prop === 'threadId')) this.#effect0();
    if (this.#hooksLive && (prop === 'agents' || prop === 'selectedKey' || prop === 'active' || prop === 'threadId' || prop === 'refreshToken')) this.#effect1();
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '5319').forEach(el => this.#listen(el, '5319', 'click', () => { sidePanel.selectedSubagent = ""; this.selectedKey = ""; this._loadedAgent = ""; }));
    this.#eventTargets(scope, '6038').forEach(el => this.#listen(el, '6038', 'click', () => { sidePanel.selectedSubagent = agent.agentPath; this._loadedAgent = ""; this.selectedKey = agent.agentPath; }));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
